import { getDocument } from "pdfjs-dist/legacy/build/pdf.mjs";
import { isPdf } from "./pdf";
import { cropImageToZones, cropPdfToZones, zoneToPdfRect, type Zone } from "./zones";

/** Afbeelding als PNG/JPEG-bytes (pdf-lib kent geen WebP). */
async function imageBytes(file: File) {
  const bmp = await createImageBitmap(file);
  const { width, height } = bmp;
  if (/^image\/(png|jpeg)$/.test(file.type)) {
    bmp.close();
    return {
      bytes: new Uint8Array(await file.arrayBuffer()),
      mime: file.type as "image/png" | "image/jpeg",
      width,
      height,
    };
  }
  const c = document.createElement("canvas");
  c.width = width;
  c.height = height;
  c.getContext("2d")!.drawImage(bmp, 0, 0);
  bmp.close();
  const blob = await new Promise<Blob>((ok, fail) =>
    c.toBlob((b) => (b ? ok(b) : fail(new Error("toBlob"))), "image/png"),
  );
  return { bytes: new Uint8Array(await blob.arrayBuffer()), mime: "image/png" as const, width, height };
}

/** Nieuwe PDF (één blad per zone) uit een gekozen tekening; klaar om te uploaden als gewone tekening. */
export async function zonesToFile(file: File, page: number, zones: Zone[]): Promise<File> {
  let out: Uint8Array;
  if (isPdf(file)) {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const task = getDocument({ data: bytes.slice() });
    try {
      const doc = await task.promise;
      const vp = (await doc.getPage(page)).getViewport({ scale: 1 });
      out = await cropPdfToZones(
        bytes,
        page - 1,
        zones.map((z) => zoneToPdfRect(z, vp)),
      );
    } finally {
      void task.destroy();
    }
  } else {
    out = await cropImageToZones(await imageBytes(file), zones);
  }
  const base = file.name.replace(/\.[^.]+$/, "");
  return new File([out as BlobPart], `${base} (zones).pdf`, { type: "application/pdf" });
}
